import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, LogOut, CalendarClock, Ticket, Clock, CheckCircle2, AlertCircle, Timer, Snowflake, X, Gift, Copy, Download, ShieldCheck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import BookingWidget from '../components/BookingWidget';
import TrainingSection from '../components/TrainingSection';
import CalculatorHistorySection from '../components/CalculatorHistorySection';
import { FeedbackDialog } from '../components/FeedbackDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { portalMe, authLogout, portalCancelBooking, portalFreezePass, portalGdprExport } from '../services/api';

const ACCESS_TOKEN_KEY = 'oc_access_token';

const GdprExportCard = () => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const data = await portalGdprExport();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `adataim-oroszcoaching-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Az adataid letöltése elindult.');
    } catch (_) {
      toast.error('A letöltés nem sikerült. Próbáld újra.');
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="portal-gdpr-card" className="mt-8 rounded-2xl border border-gray-200 bg-white p-5 md:p-6 flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
          <ShieldCheck className="text-emerald-600" size={18} />
        </div>
        <div>
          <div className="font-bold text-gray-900 text-sm">Adataim letöltése (GDPR)</div>
          <p className="text-xs text-gray-500 mt-0.5 max-w-md">
            Egy kattintással letöltheted az összes nálunk tárolt személyes adatodat
            (fiók, bérletek, foglalások, edzésterv, napló, mérések) JSON formátumban.
          </p>
        </div>
      </div>
      <Button data-testid="portal-gdpr-download-btn" variant="outline" size="sm" onClick={download} disabled={busy}
        className="border-gray-300 text-gray-700 hover:bg-gray-100">
        {busy ? <Loader2 className="mr-1.5 animate-spin" size={14} /> : <Download size={14} className="mr-1.5" />}
        Letöltés
      </Button>
    </div>
  );
};

const fmtHuDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return iso; }
};

const StatusPill = ({ status }) => {
  const map = {
    active:            { label: 'Aktív',            color: 'bg-emerald-100 text-emerald-800' },
    awaiting_payment:  { label: 'Utalásra vár',     color: 'bg-amber-100 text-amber-800' },
    expired:           { label: 'Lejárt',           color: 'bg-gray-200 text-gray-700' },
    cancelled:         { label: 'Törölve',          color: 'bg-red-100 text-red-800' },
    rolled_over:       { label: 'Átgörgetve',       color: 'bg-sky-100 text-sky-800' },
  };
  const m = map[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${m.color}`}>
      {m.label}
    </span>
  );
};

const PortalDashboardPage = () => {
  useDocumentMeta({ title: 'Fiókom | Orosz Coaching' });
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | unauth | error
  const [cancelTarget, setCancelTarget] = useState(null); // {booking_id, slot_date, slot_time}
  const [freezeOpen, setFreezeOpen] = useState(false);
  const [freezeDays, setFreezeDays] = useState(7);

  const reload = async () => {
    try {
      const d = await portalMe();
      setData(d);
      setStatus('ok');
    } catch (err) {
      if (err?.response?.status === 401) {
        setStatus('unauth');
        navigate('/fiokom/bejelentkezes', { replace: true });
      } else {
        setStatus('error');
      }
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [navigate]);

  const handleCancel = async () => {
    if (!cancelTarget) return;
    try {
      await portalCancelBooking(cancelTarget.booking_id);
      toast.success('Foglalás lemondva — az alkalom visszakerült a bérletedre.');
      setCancelTarget(null);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A lemondás nem sikerült.');
    }
  };

  const handleFreeze = async () => {
    if (!data?.active_pass) return;
    try {
      await portalFreezePass(data.active_pass.pass_id, freezeDays);
      toast.success(`Bérleted befagyasztva ${freezeDays} napra.`);
      setFreezeOpen(false);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A fagyasztás nem sikerült.');
    }
  };

  const handleLogout = async () => {
    try { await authLogout(); } catch (_) { /* non-fatal */ }
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    toast.success('Kijelentkeztél.');
    navigate('/fiokom/bejelentkezes', { replace: true });
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-yellow-600" size={32} />
      </div>
    );
  }
  if (status !== 'ok' || !data) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-6 py-24">
          <div className="text-center">
            <AlertCircle className="mx-auto text-red-500 mb-3" size={32} />
            <p className="text-gray-700 mb-4">Nem sikerült betölteni a fiókot. Próbáld újra később.</p>
            <Button onClick={() => window.location.reload()} className="bg-gray-900 text-white hover:bg-yellow-600">
              Újratöltés
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const { user, active_pass: activePass, passes } = data;
  const remaining = activePass?.remaining_sessions ?? 0;
  const total = activePass?.total_sessions ?? 0;
  const pending = activePass?.pending_deductions ?? 0;
  const daysLeft = activePass?.days_until_expiry ?? null;
  const progress = total ? Math.max(0, Math.min(100, ((total - remaining) / total) * 100)) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex flex-col">
      <Header />
      {/* Fixed Header (top-0) is ~72px tall — main content therefore needs
          at least pt-24 to clear it. Previously py-10 clipped the top row's
          "Kijelentkezés" button. */}
      <main className="flex-1 container mx-auto px-6 pt-24 md:pt-28 pb-10 md:pb-14 max-w-5xl">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-yellow-700 mb-1">Fiókom</div>
            <h1 data-testid="portal-hello" className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Szia, {user.name || user.email}!
            </h1>
            <p className="text-sm text-gray-600 mt-1">{user.email}</p>
          </div>
          <Button
            data-testid="portal-logout"
            onClick={handleLogout}
            variant="outline"
            className="border-gray-300 text-gray-700 hover:bg-gray-100"
          >
            <LogOut size={14} className="mr-2" /> Kijelentkezés
          </Button>
        </div>

        {/* Retention: loyalty coupon banner */}
        {data.active_coupons && data.active_coupons.length > 0 && (
          <div
            data-testid="portal-coupon-banner"
            className="mb-6 rounded-2xl border-2 border-dashed border-yellow-500 bg-gradient-to-br from-yellow-50 via-white to-amber-50 p-5 md:p-6 shadow-sm"
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Gift className="text-yellow-700" size={18} />
                  <span className="text-xs font-bold uppercase tracking-widest text-yellow-800">Hűségkupon aktív</span>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {data.active_coupons[0].discount_pct}% kedvezmény az új bérletedre
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Használd fel a checkoutban: <strong>{fmtHuDate(data.active_coupons[0].valid_until)}-ig érvényes</strong>. Roll-over bónusz:
                  ha most hosszabbítasz, a maradék alkalmaid átgörgetjük az új bérletre.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  data-testid="portal-coupon-copy"
                  onClick={async () => {
                    try { await navigator.clipboard.writeText(data.active_coupons[0].code); toast.success('Kuponkód másolva.'); }
                    catch { /* ignore */ }
                  }}
                  className="inline-flex items-center gap-2 rounded-lg bg-white border-2 border-yellow-500 px-4 py-2 font-mono font-bold text-lg text-yellow-800 hover:bg-yellow-50"
                >
                  {data.active_coupons[0].code}
                  <Copy size={14} />
                </button>
                <Button
                  data-testid="portal-coupon-renew-btn"
                  onClick={() => {
                    try { localStorage.setItem('oc_pending_coupon', data.active_coupons[0].code); }
                    catch { /* ignore */ }
                    window.location.href = `/?coupon=${encodeURIComponent(data.active_coupons[0].code)}#programs`;
                  }}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold"
                >
                  Meghosszabbítás →
                </Button>
              </div>
            </div>
          </div>
        )}

        {activePass ? (
          <div
            data-testid="portal-active-pass"
            className="rounded-2xl border-2 border-yellow-400 bg-gradient-to-br from-yellow-50 via-white to-amber-50 shadow-sm p-6 md:p-8 mb-8"
          >
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Ticket className="text-yellow-700" size={16} />
                  <span className="text-xs font-bold uppercase tracking-widest text-yellow-800">Aktív bérlet</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {activePass.program_title}
                </h2>
                {activePass.tier_label && (
                  <p className="text-sm text-gray-600 mt-1">{activePass.tier_label}</p>
                )}
              </div>
              <StatusPill status={activePass.status} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6">
              <div className="rounded-xl bg-white border border-yellow-200 p-4">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                  <Ticket size={12} /> Hátralévő alkalom
                </div>
                <div className="mt-2 text-4xl font-bold text-gray-900" data-testid="portal-remaining-sessions">
                  {remaining}<span className="text-lg text-gray-400"> / {total}</span>
                </div>
                {pending > 0 && (
                  <div className="text-xs text-amber-700 mt-1">
                    ebből <strong>{pending}</strong> függőben (foglalva, de még nem megjelent)
                  </div>
                )}
                <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-yellow-500 to-amber-600"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <div className="rounded-xl bg-white border border-yellow-200 p-4">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                  <CalendarClock size={12} /> Lejárati dátum
                </div>
                <div className="mt-2 text-2xl font-bold text-gray-900" data-testid="portal-expires-at">
                  {fmtHuDate(activePass.expires_at)}
                </div>
                {daysLeft !== null && (
                  <div className={`text-xs mt-1 font-semibold ${daysLeft <= 7 ? 'text-red-600' : 'text-gray-600'}`}>
                    {daysLeft > 0 ? `még ${daysLeft} nap` : 'ma jár le'}
                  </div>
                )}
              </div>

              <div className="rounded-xl bg-white border border-yellow-200 p-4">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                  <Clock size={12} /> Vásárolva
                </div>
                <div className="mt-2 text-2xl font-bold text-gray-900">
                  {fmtHuDate(activePass.purchase_date)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Fizetés: {activePass.payment_method === 'bank_transfer' ? 'banki utalás' : activePass.payment_method}
                </div>
              </div>
            </div>

            <div className="text-sm text-gray-700 border-t border-yellow-200 pt-4 flex flex-wrap items-center justify-between gap-3">
              <span>
                <strong>Foglalás:</strong> Az alábbi naptárban tudsz új alkalmat foglalni. Az alkalmakat
                csak azután vonjuk le véglegesen, hogy Gábor megerősítette, hogy megjelentél.
              </span>
              {!activePass.frozen_until && (
                <Button
                  data-testid="portal-freeze-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setFreezeOpen(true)}
                  className="border-sky-300 text-sky-800 hover:bg-sky-50 shrink-0"
                >
                  <Snowflake size={14} className="mr-1.5" /> Bérlet fagyasztása
                </Button>
              )}
              {activePass.frozen_until && (
                <span className="text-xs font-semibold text-sky-700 bg-sky-100 px-3 py-1.5 rounded-full">
                  <Snowflake size={12} className="inline-block mr-1 -mt-0.5" />
                  Fagyasztva {fmtHuDate(activePass.frozen_until)}-ig
                </span>
              )}
            </div>
          </div>
        ) : (
          <div
            data-testid="portal-no-active-pass"
            className="rounded-2xl border border-gray-200 bg-white p-6 md:p-8 text-center mb-8"
          >
            <div className="inline-flex w-14 h-14 rounded-full bg-amber-100 items-center justify-center mb-3">
              <Timer className="text-amber-700" size={26} />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
              Nincs aktív bérleted
            </h2>
            <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
              Ha most vásároltál banki utalással, várunk Gáborra, hogy megerősítse az utalás beérkezését.
              Amint aktiválva lett, itt fog megjelenni.
            </p>
            <Button onClick={() => window.location.href = '/#programs'} className="bg-yellow-600 hover:bg-yellow-700 text-white">
              Programok megnézése
            </Button>
          </div>
        )}

        {activePass && (
          <BookingWidget
            onBooked={reload}
            disabled={!!activePass.frozen_until || activePass.remaining_sessions <= 0}
            disabledReason={
              activePass.frozen_until
                ? 'A bérleted jelenleg befagyasztva. Feloldásig nem foglalhatsz új időpontot.'
                : activePass.remaining_sessions <= 0
                  ? 'Elfogytak az alkalmaid ezen a bérleten. Új vásárlással folytathatod.'
                  : ''
            }
          />
        )}

        <TrainingSection />

        <CalculatorHistorySection />

        <div className="mt-6" data-testid="portal-meal-plan-link">
          <a
            href="/fiokom/etrendem"
            className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-5 py-4 hover:border-yellow-500 hover:shadow-sm transition"
          >
            <div>
              <div className="text-sm font-semibold text-gray-900">Étrendem</div>
              <div className="text-xs text-gray-500 mt-0.5">Személyre szabott étrend, napi makró célok, hozzávalók.</div>
            </div>
            <span className="text-yellow-500 text-lg">→</span>
          </a>
        </div>

        <div className="mt-3" data-testid="portal-tools-link">
          <a
            href="/fiokom/naplo"
            className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-5 py-4 hover:border-yellow-500 hover:shadow-sm transition"
          >
            <div>
              <div className="text-sm font-semibold text-gray-900">Étkezési napló &amp; kalkulátorok</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Kalóriák és makrók naplózása, naptípus-táblázat, testzsír- és 1RM-kalkulátor.
              </div>
            </div>
            <span className="text-yellow-500 text-lg">→</span>
          </a>
        </div>

        <GdprExportCard />

        {data.upcoming_bookings && data.upcoming_bookings.length > 0 && (
          <div className="mt-8" data-testid="portal-upcoming-bookings">
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Közelgő foglalások</h3>
            <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
              {data.upcoming_bookings.map((b) => (
                <div
                  key={b.booking_id}
                  data-testid={`portal-upcoming-${b.booking_id}`}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-gray-900">
                      <Clock size={14} className="inline-block mr-1.5 -mt-0.5 text-yellow-700" />
                      {b.slot_date} · {b.slot_time}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Státusz: <span className="text-amber-700 font-semibold">függőben</span> · {fmtHuDate(b.created_at)}
                    </div>
                  </div>
                  <Button
                    data-testid={`portal-cancel-booking-${b.booking_id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => setCancelTarget(b)}
                    className="border-red-200 text-red-700 hover:bg-red-50"
                  >
                    <X size={14} className="mr-1.5" /> Lemondás
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {passes && passes.length > 0 && (
          <div data-testid="portal-history-section" className="mt-8">
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Előzmények</h3>
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              {passes.map((p, i) => (
                <div
                  key={p.pass_id}
                  data-testid={`portal-history-item-${p.pass_id}`}
                  className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${i < passes.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusPill status={p.status} />
                      <span className="font-semibold text-gray-900 text-sm">{p.program_title}</span>
                      {p.tier_label && <span className="text-xs text-gray-500">· {p.tier_label}</span>}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Vásárolva {fmtHuDate(p.purchase_date)} · {p.total_sessions} alkalom
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 text-right">
                    {p.status === 'active' && `még ${p.remaining_sessions} alk.`}
                    {p.status === 'expired' && 'lejárt'}
                    {p.status === 'awaiting_payment' && `utalás vár: ${p.payment_reference || ''}`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Cancel booking confirmation */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(v) => !v && setCancelTarget(null)}>
        <AlertDialogContent data-testid="portal-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Foglalás lemondása</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget?.slot_date} {cancelTarget?.slot_time} időpont lemondása. Az alkalom
              visszakerül a bérletedre. Lemondani legkésőbb 12 órával az időpont előtt lehet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Mégse</AlertDialogCancel>
            <AlertDialogAction
              data-testid="portal-cancel-confirm"
              onClick={handleCancel}
              className="bg-red-600 hover:bg-red-700"
            >
              Igen, lemondom
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Freeze dialog */}
      <AlertDialog open={freezeOpen} onOpenChange={setFreezeOpen}>
        <AlertDialogContent data-testid="portal-freeze-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Snowflake size={16} className="inline-block mr-2 -mt-1 text-sky-700" />
              Bérlet befagyasztása
            </AlertDialogTitle>
            <AlertDialogDescription>
              Betegség vagy utazás esetén akár 14 napra befagyaszthatod a bérleted — a lejárati idő ennyivel meghosszabbodik.
              <strong className="block mt-2 text-gray-800">Bérletenként csak egyszer használható fel!</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="text-xs uppercase tracking-widest text-gray-600">Napok száma (1–14)</Label>
            <Input
              data-testid="portal-freeze-days"
              type="number"
              min={1}
              max={14}
              value={freezeDays}
              onChange={(e) => setFreezeDays(Math.min(14, Math.max(1, parseInt(e.target.value || '1', 10))))}
              className="mt-1.5 h-11"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Mégse</AlertDialogCancel>
            <AlertDialogAction
              data-testid="portal-freeze-confirm"
              onClick={handleFreeze}
              className="bg-sky-600 hover:bg-sky-700"
            >
              Fagyasztás {freezeDays} napra
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <FeedbackDialog />
      <Footer />
    </div>
  );
};

export default PortalDashboardPage;
